import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ADMIN_CONNECTORS_QUERY_KEY,
  MAX_CONNECTOR_CATEGORIES,
  putExposure,
  useToolCategories,
  type ExposureRequest,
  type ExposureResult,
} from '../api/categories'
import { useWriteForbiddenGuard } from '../api/guard'
import { UPSTREAMS_QUERY_KEY, UPSTREAM_DETAIL_PREFIX } from '../api/mcpUpstreams'
import { titledErrorMessage } from '../api/problems'
import { ConfirmDialog } from './ConfirmDialog'
import '../views/screens.css'

interface ExposureEditorProps {
  connectorName: string
  label: string
  exposure: 'dark' | 'live' | undefined
  categories: string[] | null | undefined
  /** Extra query keys to refresh after a successful change (e.g. the upstream). */
  invalidate?: readonly (readonly unknown[])[]
  /** Upstream MCP connectors need a recorded ToS review before going live. */
  tosNote?: string
}

type Pending = { body: ExposureRequest; summary: string }

/**
 * Categories + dark/live flip for one connector (PUT /admin/connectors/{ref}/exposure).
 * Going live from dark needs 1–3 categories (the server answers 422 "Categories
 * Required" otherwise) and, for an upstream MCP connector, a recorded vendor ToS review
 * (409 "ToS Review Required"). Both refusals are shown verbatim with their remedy.
 */
export function ExposureEditor({
  connectorName,
  label,
  exposure,
  categories,
  invalidate = [],
  tosNote,
}: ExposureEditorProps) {
  const qc = useQueryClient()
  const vocab = useToolCategories()
  const current = categories ?? []
  const [selected, setSelected] = useState<string[] | null>(null)
  const chosen = selected ?? current
  const [pending, setPending] = useState<Pending | null>(null)
  const [okMsg, setOkMsg] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: (body: ExposureRequest) => putExposure(connectorName, body),
    onSuccess: (data: ExposureResult) => {
      setOkMsg(
        data.changed
          ? `${label} is now ${data.exposure} (categories: ${(data.categories ?? []).join(', ') || 'none'}).`
          : `${label}: nothing changed.`,
      )
      setSelected(null)
      // Exposure + categories are served by GET /admin/connectors AND by the upstream
      // MCP records (list + detail), so refresh all of them wherever the save happened.
      qc.invalidateQueries({ queryKey: ADMIN_CONNECTORS_QUERY_KEY })
      qc.invalidateQueries({ queryKey: UPSTREAMS_QUERY_KEY })
      qc.invalidateQueries({ queryKey: UPSTREAM_DETAIL_PREFIX })
      for (const key of invalidate) qc.invalidateQueries({ queryKey: key })
    },
    onError: () => setOkMsg(null),
  })
  useWriteForbiddenGuard(save.error)

  const toggle = (slug: string) =>
    setSelected((prev) => {
      const base = prev ?? current
      return base.includes(slug) ? base.filter((s) => s !== slug) : [...base, slug]
    })

  const withCategories = (exp: 'dark' | 'live'): ExposureRequest =>
    chosen.length > 0 ? { exposure: exp, categories: chosen } : { exposure: exp }

  const dirty =
    chosen.length !== current.length || chosen.some((s) => !current.includes(s))
  const isLive = exposure === 'live'
  const vocabList = vocab.data?.categories ?? []
  const nameOf = (slug: string) => vocabList.find((c) => c.slug === slug)?.display_name ?? slug

  return (
    <div className="scr-exposure" aria-label={`Exposure for ${label}`} role="group">
      <div className="scr-actions">
        <span>
          Exposure:{' '}
          <span className={`scr-badge scr-badge--${isLive ? 'active' : 'pending'}`}>
            {exposure ?? 'unknown'}
          </span>
        </span>
        <span className="scr-chips" aria-label="Current categories">
          {current.length === 0 && <span className="scr-muted">no categories</span>}
          {current.map((s) => (
            <span key={s} className="scr-chip">
              {nameOf(s)}
            </span>
          ))}
        </span>
      </div>

      {vocab.isError && (
        <p className="scr-err" role="alert">
          Failed to load the tool-category vocabulary (GET /admin/tool-categories).
        </p>
      )}
      {vocab.isSuccess && (
        <fieldset className="scr-fieldset">
          <legend>
            Categories (1–{MAX_CONNECTOR_CATEGORIES}; required to go live)
          </legend>
          <div className="scr-chips">
            {vocabList.map((c) => {
              const on = chosen.includes(c.slug)
              const full = !on && chosen.length >= MAX_CONNECTOR_CATEGORIES
              return (
                <label key={c.slug} className="scr-check">
                  <input
                    type="checkbox"
                    checked={on}
                    disabled={full || save.isPending}
                    onChange={() => toggle(c.slug)}
                  />
                  {c.display_name}
                </label>
              )
            })}
          </div>
        </fieldset>
      )}
      {tosNote && <p className="scr-note">{tosNote}</p>}

      <div className="scr-actions">
        {isLive && (
          <button
            type="button"
            className="screen-btn"
            disabled={!dirty || chosen.length === 0 || save.isPending}
            onClick={() =>
              setPending({
                body: { exposure: 'live', categories: chosen },
                summary: `set categories to ${chosen.join(', ')}`,
              })
            }
          >
            Save categories
          </button>
        )}
        {!isLive && (
          <>
            <button
              type="button"
              className="screen-btn"
              disabled={!dirty || chosen.length === 0 || save.isPending}
              onClick={() =>
                setPending({
                  body: { exposure: 'dark', categories: chosen },
                  summary: `set categories to ${chosen.join(', ')} (stays dark)`,
                })
              }
            >
              Save categories
            </button>
            <button
              type="button"
              className="screen-btn screen-btn-primary"
              disabled={save.isPending}
              onClick={() =>
                setPending({
                  body: withCategories('live'),
                  summary:
                    chosen.length > 0
                      ? `go LIVE with categories ${chosen.join(', ')} — visible to every workspace that passes policy`
                      : 'go LIVE without categories (the server will refuse: 1–3 are required)',
                })
              }
            >
              Go live
            </button>
          </>
        )}
        {isLive && (
          <button
            type="button"
            className="screen-btn"
            disabled={save.isPending}
            onClick={() =>
              setPending({
                body: { exposure: 'dark' },
                summary: 'go DARK — its tools disappear from every tenant on the next call',
              })
            }
          >
            Go dark
          </button>
        )}
        {okMsg && <span className="scr-ok">{okMsg}</span>}
      </div>
      {save.isError && (
        <p className="scr-err" role="alert">
          {titledErrorMessage(save.error)}
        </p>
      )}

      {pending && (
        <ConfirmDialog
          label="Confirm exposure change"
          onCancel={() => setPending(null)}
          onConfirm={() => {
            setOkMsg(null)
            save.mutate(pending.body)
            setPending(null)
          }}
        >
          <p>
            <strong>{label}</strong>: {pending.summary}?
          </p>
        </ConfirmDialog>
      )}
    </div>
  )
}
